import { ReactRenderer } from '@tiptap/react';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import tippy from 'tippy.js';

import { MentionList, MentionListHandle } from './MentionList';

export interface MentionItem {
  id: string;
  label: string;
  waPhoneNumberId: string;
  contact: string;
  foto?: string;
}

export function mentionSuggestion(
  fetchUsers: (query: string) => Promise<MentionItem[]>,
) {
  let debounceTimer: ReturnType<typeof setTimeout>;
  let previousResolves: Array<(value: MentionItem[]) => void> = [];
  let lastResults: MentionItem[] = [];
  let latestQueryId = 0;
  let isLoading = false;

  return {
    items: ({ query }: { query: string }): Promise<MentionItem[]> => {
      latestQueryId += 1;
      const currentQueryId = latestQueryId;
      isLoading = true;

      if (query === '') {
        lastResults = [];
      }

      return new Promise(resolve => {
        previousResolves.forEach(res => res(lastResults));
        previousResolves = [resolve];

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          try {
            const results = await fetchUsers(query);
            if (currentQueryId === latestQueryId) {
              lastResults = results;
              isLoading = false;
              resolve(results);
              previousResolves = [];
            }
          } catch {
            if (currentQueryId === latestQueryId) {
              isLoading = false;
              resolve([]);
              previousResolves = [];
            }
          }
        }, 200);
      });
    },

    render: () => {
      let reactRenderer: ReactRenderer<MentionListHandle>;
      let popup: ReturnType<typeof tippy>;
      let hasExited = false;

      return {
        onBeforeStart() {
          hasExited = false;
        },
        onStart(props: SuggestionProps<MentionItem>) {
          if (hasExited) return;
          if (!props.clientRect) return;
          reactRenderer = new ReactRenderer(MentionList, {
            props: { ...props, loading: isLoading },
            editor: props.editor,
          });
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: reactRenderer.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'top-start',
            theme: 'mentions',
            arrow: false,
          });
        },
        onUpdate(props: SuggestionProps<MentionItem>) {
          if (hasExited) return;
          reactRenderer?.updateProps({ ...props, loading: isLoading });
          if (!props.clientRect) return;
          if (popup?.[0] && !popup[0].state.isDestroyed) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          }
        },
        onKeyDown(props: SuggestionKeyDownProps) {
          if (props.event.key === 'Escape') {
            if (popup?.[0] && !popup[0].state.isDestroyed) {
              popup[0].hide();
            }
            return true;
          }
          return reactRenderer?.ref?.onKeyDown(props) ?? false;
        },
        onExit() {
          hasExited = true;
          if (popup?.[0] && !popup[0].state.isDestroyed) {
            popup[0].destroy();
          }
          reactRenderer?.destroy();
        },
      };
    },
  };
}
